import { TFunction } from 'i18next';

export enum ControllerExceptionCodes {
  Code_General_NotAuthorized = 'IP0001',
  Code_General_NotFound = 'IP0002',
  Code_Risk_DuplicateCode = 'RISK0001',
  Code_Asset_DuplicateCode = 'ASSET0001',
  Code_Auth_TenantAlreadyRegistered = 'AUTH0001',
  Code_Auth_TooManyLicensesAssigned = 'AUTH0002',
  Code_Auth_CannotRemoveLastAdmin = 'AUTH0003',
  Code_Auth_CannotUnlicenseLastUser = 'AUTH0004',
  Code_Auth_CannotSearchNoSites = 'AUTH0005',
  Code_Auth_NewPermissionsRequired = 'AUTH0006',
  Code_Group_DoesNotExist = 'GROUP0001',
  Code_ProcessingActivity_DuplicateCode = 'PA0001',
  Code_Graph_FileLocked = 'GRAPH0001',
  Code_Planner_InvalidConfig = 'PLANNER0001',
  Code_Task_CanOnlyMoveToArchivedFromCompletedAndRejected = 'TASK0001',
  Code_Task_CannotDeleteTemplateOfIntegration = 'TASK0002',
  Code_Task_IntegrationTemplateMustHaveTagControlFinding = 'TASK0003',
  Code_EC_AlreadyRunning = 'EC0001',
  Code_EC_CanOnlyReprocessDeadRuns = 'EC0002',
  Code_Tag_MustHaveTagsForEachSystemType = 'TAG0001',
  Code_Task_StartDateBeforeLastCompletedInstance = 'TASK0004',
  Code_Task_EndDateAfterNextInstance = 'TASK0005',
  Code_Integration_MissingGraphPermission = 'INTEGRATION0001',
  Code_Integration_NoGraphPermissions = 'INTEGRATION0002',
  Code_Integration_AuthFailureGeneric = 'INTEGRATION0003',
  Code_Report_SharePointReportsDisabled = 'REPORT0001',
  Code_Report_SharePointPermissionsMissing = 'REPORT0002',
  Code_TaskView_DuplicateName = 'TASKVIEW0001',
  Code_Billing_SubscriptionRequired = 'BILLING0001',
  Code_Library_TargetNotFound = 'LIBRARY0001',
  Code_Library_NoWritePermission = 'LIBRARY0002',
  Code_Library_TargetLocked = 'LIBRARY0003',
  Code_Library_DuplicateExternalId = 'LIBRARY0004',
  Code_Asset_DuplicateExternalId = 'ASSET0002',
  Code_Task_DuplicateExternalId = 'TASK0006',
  Code_Process_DuplicateExternalId = 'PROCESS0001',
  Code_Objective_DuplicateExternalId = 'OBJECTIVE0001',
  Code_Risk_DuplicateExternalId = 'RISK0002',
  Code_OrgUnit_BindNotAllowed = 'ORGUNIT0001',
  Code_OrgUnit_BindDirectoryNotFound = 'ORGUNIT0003',
  Code_OrgUnit_BindContainerNotAllowed = 'ORGUNIT0005',
  Code_OrgUnit_BindDirectoryInUse = 'ORGUNIT0006',
  Code_Task_EndDateAfterDeadline = 'TASK0007',
  Code_Standard_MaxStandardsReached = 'STANDARD0001',
}

export default class ControllerException {
  code: string;

  message?: string | undefined;

  constructor() {
    this.code = '';
  }

  // `message` carries the exception's Message field from the API, used for codes
  // whose i18n template needs dynamic data. Backwards-compatible for existing
  // 2-arg call sites: when message is undefined, templates with placeholders
  // render with the placeholder left as-is (same as before).
  static getMessage(code: ControllerExceptionCodes, t: TFunction<string[]>, message?: string): string {
    switch (code) {
      case ControllerExceptionCodes.Code_General_NotAuthorized:
        return t('exceptions:General.NoAuth');
      case ControllerExceptionCodes.Code_General_NotFound:
        return t('exceptions:General.NotFound');
      case ControllerExceptionCodes.Code_Task_CanOnlyMoveToArchivedFromCompletedAndRejected:
        return t('exceptions:Task.CanOnlyMoveToArchivedFromCompleted');
      case ControllerExceptionCodes.Code_Risk_DuplicateCode:
        return t('exceptions:Risk.DuplicateCode');
      case ControllerExceptionCodes.Code_Asset_DuplicateCode:
        return t('exceptions:Asset.DuplicateCode');
      case ControllerExceptionCodes.Code_Auth_TenantAlreadyRegistered:
        return t('exceptions:Auth.TenantAlreadyRegistered');
      case ControllerExceptionCodes.Code_Auth_TooManyLicensesAssigned:
        return t('exceptions:Auth.TooManyLicensesAssigned');
      case ControllerExceptionCodes.Code_Auth_CannotRemoveLastAdmin:
        return t('exceptions:Auth.CannotRemoveLastAdmin');
      case ControllerExceptionCodes.Code_Auth_CannotUnlicenseLastUser:
        return t('exceptions:Auth.CannotUnlicenseLastUser');
      case ControllerExceptionCodes.Code_Auth_CannotSearchNoSites:
        return t('exceptions:Auth.CannotSearchNoSites');
      case ControllerExceptionCodes.Code_Auth_NewPermissionsRequired:
        return t('exceptions:Auth.NewPermissionsRequired');
      case ControllerExceptionCodes.Code_Group_DoesNotExist:
        return t('exceptions:Group.NotFound');
      case ControllerExceptionCodes.Code_ProcessingActivity_DuplicateCode:
        return t('exceptions:ProcessingActivity.DuplicateCode');
      case ControllerExceptionCodes.Code_Graph_FileLocked:
        return t('sharepoint:PublicationStatus.Locked');
      case ControllerExceptionCodes.Code_Planner_InvalidConfig:
        return t('adminCompany:Tabs.Integration.Planner.Errors.InvalidConfig');
      case ControllerExceptionCodes.Code_Task_CannotDeleteTemplateOfIntegration:
        return t('exceptions:Task.CannotDeleteTemplateOfIntegration');
      case ControllerExceptionCodes.Code_Task_IntegrationTemplateMustHaveTagControlFinding:
        return t('exceptions:Task.IntegrationTemplateMustHaveTagControlFinding');
      case ControllerExceptionCodes.Code_Tag_MustHaveTagsForEachSystemType:
        return t('exceptions:Tag.MustHaveTagsForEachSystemType');
      case ControllerExceptionCodes.Code_Task_StartDateBeforeLastCompletedInstance: {
        // API sends the lower-bound date in Message as an ISO-8601 ("o") string.
        // Format in the user's locale via Intl.DateTimeFormat (honours the browser
        // locale; no date library dependency added).
        let minDate = message ?? '';
        if (message) {
          const parsed = new Date(message);
          if (!Number.isNaN(parsed.getTime())) {
            minDate = new Intl.DateTimeFormat(undefined, { dateStyle: 'long' }).format(parsed);
          }
        }

        return t('exceptions:Task.StartDateBeforeLastCompletedInstance', { minDate });
      }
      case ControllerExceptionCodes.Code_Task_EndDateAfterNextInstance: {
        // API sends the upper-bound date in Message as an ISO-8601 ("o") string.
        // Format in the user's locale via Intl.DateTimeFormat (honours the browser
        // locale; no date library dependency added).
        let maxDate = message ?? '';
        if (message) {
          const parsed = new Date(message);
          if (!Number.isNaN(parsed.getTime())) {
            maxDate = new Intl.DateTimeFormat(undefined, { dateStyle: 'long' }).format(parsed);
          }
        }

        return t('exceptions:Task.EndDateAfterNextInstance', { maxDate });
      }
      case ControllerExceptionCodes.Code_Integration_MissingGraphPermission:
        // API sends the missing Microsoft Graph permission name (e.g. "AuditLog.Read.All")
        // in Message. The i18n template names it so the customer admin knows exactly
        // which permission to grant, instead of a generic "Error during test run" toast.
        return t('exceptions:Integration.MissingGraphPermission', { permission: message ?? '' });
      case ControllerExceptionCodes.Code_Integration_NoGraphPermissions:
        // The integration's app has no Graph permissions consented at all -- different
        // remediation than INTEGRATION0001 (configure the app, not just grant one).
        return t('exceptions:Integration.NoGraphPermissions');
      case ControllerExceptionCodes.Code_Integration_AuthFailureGeneric:
        // Any other Graph auth failure (HTTP 403, conditional access, expired token, ...).
        // Surface the classifier's message verbatim so the user has at least one actionable
        // detail to share with their admin/support.
        return t('exceptions:Integration.AuthFailureGeneric', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Report_SharePointReportsDisabled:
        return t('exceptions:Report.SharePointReportsDisabled');
      case ControllerExceptionCodes.Code_Report_SharePointPermissionsMissing:
        return t('exceptions:Report.SharePointPermissionsMissing');
      case ControllerExceptionCodes.Code_TaskView_DuplicateName:
        return t('exceptions:TaskView.DuplicateName');
      case ControllerExceptionCodes.Code_Billing_SubscriptionRequired:
        return t('exceptions:Billing.SubscriptionRequired');
      case ControllerExceptionCodes.Code_Library_TargetNotFound:
        return t('exceptions:Library.TargetNotFound');
      case ControllerExceptionCodes.Code_Library_NoWritePermission:
        return t('exceptions:Library.NoWritePermission');
      case ControllerExceptionCodes.Code_Library_TargetLocked:
        return t('exceptions:Library.TargetLocked');
      case ControllerExceptionCodes.Code_Library_DuplicateExternalId:
        return t('exceptions:Library.DuplicateExternalId');
      case ControllerExceptionCodes.Code_Asset_DuplicateExternalId:
        return t('exceptions:Asset.DuplicateExternalId');
      case ControllerExceptionCodes.Code_Task_DuplicateExternalId:
        return t('exceptions:Task.DuplicateExternalId');
      case ControllerExceptionCodes.Code_Process_DuplicateExternalId:
        return t('exceptions:Process.DuplicateExternalId');
      case ControllerExceptionCodes.Code_Objective_DuplicateExternalId:
        return t('exceptions:Objective.DuplicateExternalId');
      case ControllerExceptionCodes.Code_Risk_DuplicateExternalId:
        return t('exceptions:Risk.DuplicateExternalId');
      case ControllerExceptionCodes.Code_OrgUnit_BindNotAllowed:
        return t('exceptions:OrgUnit.BindNotAllowed');
      case ControllerExceptionCodes.Code_OrgUnit_BindContainerNotAllowed:
        return t('exceptions:OrgUnit.BindContainerNotAllowed');
      case ControllerExceptionCodes.Code_OrgUnit_BindDirectoryInUse:
        return t('exceptions:OrgUnit.BindDirectoryInUse');
      case ControllerExceptionCodes.Code_OrgUnit_BindDirectoryNotFound:
        return t('exceptions:OrgUnit.BindDirectoryNotFound');
      case ControllerExceptionCodes.Code_Task_EndDateAfterDeadline:
        return t('exceptions:Task.EndDateAfterDeadline');
      case ControllerExceptionCodes.Code_Standard_MaxStandardsReached:
        return t('exceptions:Standard.MaxStandardsReached');
      default:
        return t('exceptions:General.UnknownError');
    }
  }
}
